import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { Plus, Search, MoreHorizontal, Eye, Pencil, Trash, CheckSquare, Calendar as CalendarIcon, Building2, Filter, User, List, ChevronLeft, ChevronRight, BarChart3, Send, AlertTriangle } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { useUser } from '../context/UserContext';
import TaskDialog from '../components/TaskDialog';
import TaskCompletionDialog from '../components/TaskCompletionDialog';
import TaskDetailDialog from '../components/TaskDetailDialog';
import ExportMenu from '../components/ExportMenu';
import ClientTypeFilter from '../components/ClientTypeFilter';
import { matchesClientTypeFilter } from '../utils/clientType';
import { TasksAnalytics } from '../components/analytics';
import RemindDialog from '../components/RemindDialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DAYS_ES = ['Lu', 'Ma', 'Mi', 'Ju', 'Vi', 'Sa', 'Do'];
const MONTHS_ES = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];

// Normalize task status (matches backend STATUS_MAP): open/pending/todo/in_progress → pending; completed/done/closed → completed
const isOpen = (status) => {
  if (!status) return false;
  const s = String(status).trim().toLowerCase();
  return s === 'open' || s === 'pending' || s === 'todo' || s === 'in_progress';
};

const Tasks = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isEditor, isAdmin, editorUsers, currentUser } = useUser();
  const [tasks, setTasks] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [typsaOrgUnits, setTypsaOrgUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  // Reminder-by-email state (admin only)
  const [selectedForReminder, setSelectedForReminder] = useState(new Set());
  const [remindDialogOpen, setRemindDialogOpen] = useState(false);
  const [remindItems, setRemindItems] = useState([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [ownerFilter, setOwnerFilter] = useState('me'); // Por defecto "Yo"
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [viewMode, setViewMode] = useState('calendar');
  const [selectedDate, setSelectedDate] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  // Feb 2026: drag-and-drop from "Sin fecha" tray onto a calendar day cell.
  const [draggingTaskId, setDraggingTaskId] = useState(null);
  const [hoverDropDate, setHoverDropDate] = useState(null);
  const [completionDialogOpen, setCompletionDialogOpen] = useState(false);
  const [completingTask, setCompletingTask] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [viewingTask, setViewingTask] = useState(null);
  
  // Client type filter state
  const [clientTypeFilter, setClientTypeFilter] = useState(['typsa_gg', 'typsa_colab', 'privada', 'publica']);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [tasksRes, companiesRes, typsaUnitsRes] = await Promise.all([
        axios.get(`${API}/tasks`),
        axios.get(`${API}/companies`),
        axios.get(`${API}/typsa-org-units`)
      ]);
      setTasks(tasksRes.data);
      setCompanies(companiesRes.data);
      setTypsaOrgUnits(typsaUnitsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Auto-open task from URL ?id= param (called from Dashboard)
  useEffect(() => {
    const idFromUrl = searchParams.get('id');
    if (idFromUrl && tasks.length > 0) {
      const found = tasks.find(t => t.id === idFromUrl);
      if (found) {
        setViewingTask(found);
        setDetailDialogOpen(true);
        setSearchParams(prev => { prev.delete('id'); return prev; }, { replace: true });
      }
    }
  }, [tasks, searchParams]);

  const getCompanyName = (companyId) => {
    if (!companyId) return 'Sin empresa';
    const company = companies.find(c => c.id === companyId);
    return company ? company.name : 'Empresa no encontrada';
  };

  // Get company or TYPSA unit name for a task
  const getTaskEntityName = (task) => {
    if (task.typsa_org_unit_id) {
      const unit = typsaOrgUnits.find(u => u.id === task.typsa_org_unit_id);
      return unit ? `TYPSA: ${unit.value || unit.name}` : 'Unidad TYPSA no encontrada';
    }
    if (task.company_id) {
      const company = companies.find(c => c.id === task.company_id);
      return company ? company.name : 'Empresa no encontrada';
    }
    return 'Sin especificar';
  };

  // Toggle client type filter
  const toggleClientTypeFilter = (type) => {
    setClientTypeFilter(prev => {
      if (prev.includes(type)) {
        return prev.filter(t => t !== type);
      } else {
        return [...prev, type];
      }
    });
  };

  // Determine task's client type
  const getTaskClientType = (task) => {
    if (task.typsa_org_unit_id) {
      return 'typsa';
    }
    const company = companies.find(c => c.id === task.company_id);
    if (company) {
      if (company.company_type === 'typsa' || company.name?.toUpperCase().includes('TYPSA')) {
        return 'typsa';
      }
      return company.company_type; // 'privada' or 'publica'
    }
    return null;
  };

  const handleView = (task) => {
    if (task.company_id) {
      navigate(`/companies/${task.company_id}`);
      return;
    }
    toast.info(`${task.title}\nEstado: ${task.status}\nFecha: ${task.due_date || 'Sin fecha'}`);
  };

  const handleEdit = (task) => {
    setEditingTask(task);
    setDialogOpen(true);
  };

  const handleToggleStatus = async (task) => {
    // If marking as completed, open the completion dialog
    if (isOpen(task.status)) {
      setCompletingTask(task);
      setCompletionDialogOpen(true);
      return;
    }
    
    // If marking as Open (reopening), just update the status
    try {
      await axios.put(`${API}/tasks/${task.id}`, { ...task, status: 'Open' });
      toast.success('Tarea marcada como Pendiente');
      fetchData();
    } catch (error) {
      toast.error('Error al actualizar la tarea');
    }
  };

  const handleCompletionSuccess = () => {
    fetchData();
    setCompletingTask(null);
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/tasks/${deleteConfirm.id}`);
      toast.success('Tarea eliminada');
      fetchData();
    } catch (error) {
      toast.error('Error al eliminar la tarea');
    } finally {
      setDeleting(false);
      setDeleteConfirm(null);
    }
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setEditingTask(null);
  };

  const handleDialogSuccess = () => {
    fetchData();
    handleDialogClose();
  };

  // Feb 2026: assign a date to a task via drag-and-drop from the "Sin fecha"
  // tray onto a calendar day cell. Optimistic UI update + PUT.
  const handleDropTaskOnDay = async (taskId, dateStr) => {
    const task = tasks.find((t) => t.id === taskId);
    if (!task) return;
    // Optimistic patch so the card jumps instantly to the new cell.
    setTasks((prev) => prev.map((t) => t.id === taskId ? { ...t, due_date: dateStr, due_date_defaulted: false } : t));
    try {
      // PUT expects the full task shape (see /api/tasks::update_task).
      await axios.put(`${API}/tasks/${taskId}`, { ...task, due_date: dateStr, due_date_defaulted: false });
      toast.success(`Fecha asignada: ${dateStr}`);
    } catch (err) {
      // Revert if the request fails.
      setTasks((prev) => prev.map((t) => t.id === taskId ? { ...t, due_date: task.due_date } : t));
      toast.error('No se pudo asignar la fecha');
    }
  };

  const handleViewTask = (task) => {
    setViewingTask(task);
    setDetailDialogOpen(true);
  };

  // Calendar helpers - with client type filter.
  // Feb 2026: compare using the YYYY-MM-DD prefix so datetime-shaped due_date
  // values (e.g. "2026-08-19T00:00:00") still match. Previously a strict
  // equality caused voice-note-generated tasks to vanish from the calendar
  // while still showing in the list.
  const getTasksForDate = (dateStr) => {
    return tasks.filter(task => {
      const dueDay = task.due_date ? String(task.due_date).slice(0, 10) : '';
      if (dueDay !== dateStr) return false;
      const clientType = getTaskClientType(task);
      return !clientType || matchesClientTypeFilter(clientType, clientTypeFilter);
    });
  };

  // Tasks without due_date shown in a dedicated "Sin fecha" tray in the
  // calendar view. They still respect the client type filter so the count
  // stays consistent with the sidebar filter chips.
  const getTasksWithoutDate = () => {
    return tasks.filter(task => {
      if (task.due_date) return false;
      const clientType = getTaskClientType(task);
      return !clientType || matchesClientTypeFilter(clientType, clientTypeFilter);
    });
  };

  const getTaskCountForDate = (dateStr) => {
    const dateTasks = getTasksForDate(dateStr);
    return {
      open: dateTasks.filter(t => isOpen(t.status)).length,
      completed: dateTasks.filter(t => t.status === 'Completed').length
    };
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    let startDay = firstDay.getDay() - 1;
    if (startDay < 0) startDay = 6;
    
    const days = [];
    
    // Previous month days
    const prevMonthLastDay = new Date(year, month, 0).getDate();
    for (let i = startDay - 1; i >= 0; i--) {
      const day = prevMonthLastDay - i;
      const prevMonth = month === 0 ? 11 : month - 1;
      const prevYear = month === 0 ? year - 1 : year;
      days.push({
        day,
        date: `${prevYear}-${String(prevMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`,
        isCurrentMonth: false
      });
    }
    
    // Current month days
    for (let i = 1; i <= daysInMonth; i++) {
      days.push({
        day: i,
        date: `${year}-${String(month + 1).padStart(2, '0')}-${String(i).padStart(2, '0')}`,
        isCurrentMonth: true
      });
    }
    
    // Next month days
    const remainingDays = 42 - days.length;
    for (let i = 1; i <= remainingDays; i++) {
      const nextMonth = month === 11 ? 0 : month + 1;
      const nextYear = month === 11 ? year + 1 : year;
      days.push({
        day: i,
        date: `${nextYear}-${String(nextMonth + 1).padStart(2, '0')}-${String(i).padStart(2, '0')}`,
        isCurrentMonth: false
      });
    }
    
    return days;
  };

  const getNextMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 1);
  };

  const goToPrevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };

  const goToNextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };

  const goToToday = () => {
    setCurrentMonth(new Date());
    const today = new Date().toISOString().split('T')[0];
    setSelectedDate(today);
  };

  const today = new Date().toISOString().split('T')[0];
  const selectedDateTasks = selectedDate ? getTasksForDate(selectedDate) : [];

  // Get unique owners for filter
  const uniqueOwners = [...new Set(tasks.map(t => t.owner).filter(Boolean))].sort((a, b) => a.localeCompare(b, 'es'));

  // Filter tasks by search, status, owner and client type
  const filteredTasks = useMemo(() => {
    const filtered = tasks.filter(task => {
      // Filter by client type
      const clientType = getTaskClientType(task);
      if (clientType && !matchesClientTypeFilter(clientType, clientTypeFilter)) {
        return false;
      }
      
      const searchLower = search.toLowerCase();
      const matchesSearch = (
        task.title?.toLowerCase().includes(searchLower) ||
        getTaskEntityName(task).toLowerCase().includes(searchLower)
      );
      const matchesStatus = statusFilter === 'all' || task.status === statusFilter;
      
      // Handle owner filter
      let matchesOwner = true;
      if (ownerFilter === 'me') {
        matchesOwner = task.owner === currentUser?.name;
      } else if (ownerFilter === 'unassigned') {
        matchesOwner = !task.owner || task.owner.trim() === '';
      } else if (ownerFilter !== 'all') {
        matchesOwner = task.owner === ownerFilter;
      }
      
      return matchesSearch && matchesStatus && matchesOwner;
    });
    
    // Sort: Open tasks first, then by due_date
    return filtered.sort((a, b) => {
      // First priority: Open tasks before Completed
      if (isOpen(a.status) && !isOpen(b.status)) return -1;
      if (!isOpen(a.status) && isOpen(b.status)) return 1;
      
      // Second priority: Earlier due dates first (for Open tasks)
      if (isOpen(a.status) && isOpen(b.status)) {
        if (a.due_date && b.due_date) {
          return a.due_date.localeCompare(b.due_date);
        }
        if (a.due_date && !b.due_date) return -1;
        if (!a.due_date && b.due_date) return 1;
      }
      
      return 0;
    });
  }, [tasks, search, statusFilter, ownerFilter, clientTypeFilter, companies, typsaOrgUnits, currentUser]);

  // Filtered counts — MUST be derived from the same base (filteredTasks) so total = open + completed.
  const openCount = useMemo(
    () => filteredTasks.filter(t => isOpen(t.status)).length,
    [filteredTasks]
  );

  const completedCount = useMemo(
    () => filteredTasks.filter(t => !isOpen(t.status)).length,
    [filteredTasks]
  );

  // Calendar Month Component
  const CalendarMonth = ({ date, showHeader = true }) => {
    const days = getDaysInMonth(date);
    const monthName = MONTHS_ES[date.getMonth()];
    const year = date.getFullYear();

    return (
      <div className="select-none">
        {showHeader && (
          <div className="text-center mb-4">
            <h3 className="font-semibold text-lg">{monthName} {year}</h3>
          </div>
        )}
        <div className="grid grid-cols-7 gap-1 mb-2">
          {DAYS_ES.map(day => (
            <div key={day} className="text-center text-xs font-medium text-muted-foreground py-2">
              {day}
            </div>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-1">
          {days.map((dayInfo, idx) => {
            const taskCount = getTaskCountForDate(dayInfo.date);
            const isToday = dayInfo.date === today;
            const isSelected = dayInfo.date === selectedDate;
            const hasOpenTasks = taskCount.open > 0;
            const hasCompletedOnly = taskCount.completed > 0 && taskCount.open === 0;

            return (
              <button
                key={idx}
                onClick={() => setSelectedDate(dayInfo.date)}
                data-testid={`calendar-day-${dayInfo.date}`}
                data-open-count={taskCount.open}
                data-completed-count={taskCount.completed}
                onDragOver={draggingTaskId && dayInfo.isCurrentMonth ? (e) => {
                  e.preventDefault();
                  e.dataTransfer.dropEffect = 'move';
                  if (hoverDropDate !== dayInfo.date) setHoverDropDate(dayInfo.date);
                } : undefined}
                onDragLeave={draggingTaskId ? () => {
                  if (hoverDropDate === dayInfo.date) setHoverDropDate(null);
                } : undefined}
                onDrop={draggingTaskId && dayInfo.isCurrentMonth ? (e) => {
                  e.preventDefault();
                  const taskId = e.dataTransfer.getData('text/task-id');
                  setHoverDropDate(null);
                  setDraggingTaskId(null);
                  if (taskId) handleDropTaskOnDay(taskId, dayInfo.date);
                } : undefined}
                className={`
                  relative aspect-square p-1 rounded-lg text-sm transition-all
                  ${!dayInfo.isCurrentMonth ? 'text-muted-foreground/40' : 'text-foreground'}
                  ${isToday ? 'ring-2 ring-primary ring-offset-2' : ''}
                  ${isSelected ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'}
                  ${draggingTaskId && dayInfo.isCurrentMonth ? 'ring-2 ring-dashed ring-primary/40 cursor-copy' : ''}
                  ${hoverDropDate === dayInfo.date ? 'bg-primary/20 ring-primary' : ''}
                `}
              >
                <span className="relative z-10">{dayInfo.day}</span>
                {(hasOpenTasks || hasCompletedOnly) && (
                  <div className="absolute bottom-1 left-1/2 -translate-x-1/2 flex gap-0.5">
                    {hasOpenTasks && (
                      <div className={`w-1.5 h-1.5 rounded-full ${isSelected ? 'bg-primary-foreground' : 'bg-red-500'}`} />
                    )}
                    {hasCompletedOnly && (
                      <div className={`w-1.5 h-1.5 rounded-full ${isSelected ? 'bg-primary-foreground' : 'bg-green-500'}`} />
                    )}
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  // Task Card Component. `draggable` is opt-in and used from the "Sin fecha"
  // tray so users can drag a task onto a calendar day cell to assign a date.
  const TaskCard = ({ task, compact = false, draggable = false }) => {
    const isPending = isOpen(task.status);
    const isSelected = selectedForReminder.has(task.id);
    return (
    <div
      className={`
        group relative bg-card border rounded-lg transition-all hover:shadow-md cursor-pointer
        ${compact ? 'p-3' : 'p-4'}
        ${isSelected ? 'ring-2 ring-primary/60' : ''}
        ${draggable ? 'cursor-grab active:cursor-grabbing hover:border-primary' : ''}
      `}
      onClick={() => handleViewTask(task)}
      data-testid={`task-card-${task.id}`}
      draggable={draggable && isEditor}
      onDragStart={draggable && isEditor ? (e) => {
        e.dataTransfer.setData('text/task-id', task.id);
        e.dataTransfer.effectAllowed = 'move';
        setDraggingTaskId(task.id);
      } : undefined}
      onDragEnd={draggable && isEditor ? () => setDraggingTaskId(null) : undefined}
    >
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="absolute top-2 right-2 h-7 w-7 opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity"
            onClick={(e) => e.stopPropagation()}
          >
            <MoreHorizontal className="w-4 h-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-48" onClick={(e) => e.stopPropagation()}>
          {isEditor && (
            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleToggleStatus(task); }}>
              <CheckSquare className="w-4 h-4 mr-2" />
              Marcar como {isOpen(task.status) ? 'Completada' : 'Pendiente'}
            </DropdownMenuItem>
          )}
          <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleView(task); }}>
            <Eye className="w-4 h-4 mr-2" />
            Ver empresa
          </DropdownMenuItem>
          {isEditor && (
            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleEdit(task); }}>
              <Pencil className="w-4 h-4 mr-2" />
              Editar
            </DropdownMenuItem>
          )}
          {isEditor && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={(e) => { e.stopPropagation(); setDeleteConfirm(task); }} className="text-destructive">
                <Trash className="w-4 h-4 mr-2" />
                Eliminar
              </DropdownMenuItem>
            </>
          )}
          {isAdmin && isPending && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={(e) => { e.stopPropagation(); openReminderForSingle(task); }}>
                <Send className="w-4 h-4 mr-2" />
                Recordar por correo
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      <div className="flex items-start gap-3 pr-8">
        {isAdmin && isPending && (
          <div className="pt-0.5" onClick={(e) => e.stopPropagation()}>
            <Checkbox
              checked={isSelected}
              onCheckedChange={() => toggleReminderSelection(task.id)}
              aria-label="Seleccionar para recordar por correo"
              data-testid={`task-remind-checkbox-${task.id}`}
            />
          </div>
        )}
        <button
          onClick={(e) => { e.stopPropagation(); isEditor && handleToggleStatus(task); }}
          disabled={!isEditor}
          className={`
            w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5 transition-all
            ${task.status === 'Completed' 
              ? 'bg-green-500 border-green-500 text-white' 
              : isEditor 
                ? 'border-muted-foreground/50 hover:border-primary hover:bg-primary/10'
                : 'border-muted-foreground/30 cursor-not-allowed'
            }
          `}
        >
          {task.status === 'Completed' && (
            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
            </svg>
          )}
        </button>
        <div className="flex-1 min-w-0">
          <div className={`font-medium ${compact ? 'text-sm' : ''} ${task.status === 'Completed' ? 'line-through text-muted-foreground' : ''}`}>
            {task.title}
          </div>
          <div className={`flex flex-wrap items-center gap-2 mt-1.5 ${compact ? 'text-xs' : 'text-sm'}`}>
            {task.due_date && (() => {
              // Defensive: any legacy datetime-shaped value ("2026-08-19T00:00:00")
              // is shown as its date-only prefix. Also drives overdue colour off the
              // same normalized string so it can never miscolour.
              const dueDay = String(task.due_date).slice(0, 10);
              return (
                <span className={`flex items-center gap-1 ${dueDay < today && isOpen(task.status) ? 'text-red-500' : 'text-muted-foreground'}`}>
                  <CalendarIcon className="w-3 h-3" />
                  {dueDay}
                  {task.due_date_defaulted && (
                    <span
                      className="ml-1 text-[9px] uppercase tracking-wide px-1 py-px rounded bg-amber-100 text-amber-800 border border-amber-200"
                      title="Fecha sugerida automáticamente al crear desde nota de voz. Ajústala si es necesario."
                    >
                      Sugerida
                    </span>
                  )}
                </span>
              );
            })()}
            <span className="flex items-center gap-1 text-muted-foreground">
              <Building2 className="w-3 h-3" />
              {getTaskEntityName(task)}
            </span>
            {task.owner && (
              <span className="flex items-center gap-1 text-muted-foreground">
                <User className="w-3 h-3" />
                {task.owner}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
    );
  };

  // ─── Reminder helpers (admin only) ───
  const toggleReminderSelection = (id) => {
    setSelectedForReminder(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const resolveOwnerEmail = (ownerName) => {
    if (!ownerName) return '';
    const match = (editorUsers || []).find(u => (u.name || '').trim().toLowerCase() === ownerName.trim().toLowerCase());
    return match?.email || '';
  };

  const buildReminderItemFromTask = (t) => ({
    type: 'task',
    id: t.id,
    title: t.title,
    owner_name: t.owner || 'Sin responsable',
    owner_email: t.assignee_email || resolveOwnerEmail(t.owner),
  });

  const openReminderForSingle = (task) => {
    setRemindItems([buildReminderItemFromTask(task)]);
    setRemindDialogOpen(true);
  };

  const openReminderForSelected = () => {
    const byId = new Map(tasks.map(t => [t.id, t]));
    const chosen = Array.from(selectedForReminder).map(id => byId.get(id)).filter(Boolean).map(buildReminderItemFromTask);
    if (chosen.length === 0) {
      toast.error('Selecciona al menos una tarea');
      return;
    }
    setRemindItems(chosen);
    setRemindDialogOpen(true);
  };

  const handleReminderSent = () => {
    setSelectedForReminder(new Set());
  };

  return (
    <div className="p-4 md:p-8" data-testid="tasks-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-semibold tracking-tight">Tareas</h1>
          <p className="text-sm text-muted-foreground mt-1" data-testid="tasks-header-count">
            <span className="font-medium">
              {ownerFilter === 'me'
                ? `Yo (${currentUser?.name?.split(' ')[0] || 'Mi usuario'})`
                : ownerFilter === 'all'
                  ? 'Todo el equipo'
                  : ownerFilter === 'unassigned'
                    ? 'Sin asignar'
                    : ownerFilter}
            </span>
            {' · '}{filteredTasks.length} tareas • {openCount} pendientes • {completedCount} completadas
            {clientTypeFilter.length < 4 && <span className="text-xs ml-1">(filtrado)</span>}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex rounded-lg border p-1 bg-muted/30">
            <Button
              variant={viewMode === 'list' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('list')}
              className="h-8 px-3"
              data-testid="tasks-list-tab"
            >
              <List className="w-4 h-4 mr-2" />
              Lista
            </Button>
            <Button
              variant={viewMode === 'calendar' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('calendar')}
              className="h-8 px-3"
              data-testid="tasks-calendar-tab"
            >
              <CalendarIcon className="w-4 h-4 mr-2" />
              Calendario
            </Button>
            <Button
              variant={viewMode === 'analytics' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('analytics')}
              className="h-8 px-3"
              data-testid="tasks-analytics-tab"
            >
              <BarChart3 className="w-4 h-4 mr-2" />
              Analíticas
            </Button>
          </div>
          <Select value={ownerFilter} onValueChange={setOwnerFilter}>
            <SelectTrigger className="w-48 h-8 text-xs" data-testid="tasks-owner-filter-shared">
              <User className="w-3.5 h-3.5 mr-2" />
              <SelectValue placeholder="Responsable" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="me">Yo ({currentUser?.name?.split(' ')[0] || 'Mi usuario'})</SelectItem>
              <SelectItem value="all">Todos los responsables</SelectItem>
              <SelectItem value="unassigned">Sin asignar</SelectItem>
              {uniqueOwners.filter(o => o !== currentUser?.name).map(owner => (
                <SelectItem key={owner} value={owner}>{owner}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <ClientTypeFilter
            selectedTypes={clientTypeFilter}
            onToggle={toggleClientTypeFilter}
          />
          <ExportMenu endpoint="tasks" />
          {isAdmin && selectedForReminder.size > 0 && (
            <Button
              variant="default"
              size="sm"
              onClick={openReminderForSelected}
              data-testid="tasks-remind-selected-btn"
            >
              <Send className="w-4 h-4 mr-2" />
              Recordar ({selectedForReminder.size})
            </Button>
          )}
          {isEditor && (
            <Button onClick={() => { setEditingTask(null); setDialogOpen(true); }}>
              <Plus className="w-4 h-4 mr-2" />
              Nueva Tarea
            </Button>
          )}
        </div>
      </div>

      {viewMode === 'analytics' ? (
        <TasksAnalytics
          clientTypeFilter={clientTypeFilter}
          ownerFilter={ownerFilter}
          ownerName={currentUser?.name}
        />
      ) : viewMode === 'list' ? (
        <>
          {/* Search and Filter */}
          <div className="flex flex-col sm:flex-row gap-3 mb-6">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                <Input
                  placeholder="Buscar tareas..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10"
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-full sm:w-40" data-testid="tasks-status-filter">
                  <Filter className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Estado" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas</SelectItem>
                  <SelectItem value="Open">Pendientes</SelectItem>
                  <SelectItem value="Completed">Completadas</SelectItem>
                </SelectContent>
              </Select>
              {(statusFilter !== 'all' || ownerFilter !== 'me') && (
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => { setStatusFilter('all'); setOwnerFilter('me'); }}
                  className="text-muted-foreground hover:text-foreground"
                  data-testid="clear-filters-btn"
                >
                  Limpiar filtros
                </Button>
              )}
          </div>

          {loading ? (
            <div className="text-sm text-muted-foreground">Cargando...</div>
          ) : filteredTasks.length === 0 ? (
            <div className="text-center py-12">
              <CheckSquare className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground mb-4">
                {search || statusFilter !== 'all' || ownerFilter !== 'me' ? 'No se encontraron tareas con los filtros seleccionados' : 'No tienes tareas asignadas'}
              </p>
              {!search && statusFilter === 'all' && ownerFilter === 'me' && isEditor && (
                <Button onClick={() => setDialogOpen(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  Crear primera tarea
                </Button>
              )}
            </div>
          ) : (
            <div className="space-y-6">
              {/* Tareas Pendientes - Siempre arriba */}
              {(() => {
                const pendingTasks = filteredTasks.filter(t => isOpen(t.status));
                const completedTasks = filteredTasks.filter(t => !isOpen(t.status));
                
                // Agrupar completadas por mes
                const tasksByMonth = completedTasks.reduce((acc, task) => {
                  const date = task.due_date || task.created_at?.split('T')[0];
                  if (!date) {
                    const key = 'sin-fecha';
                    if (!acc[key]) acc[key] = { label: 'Sin fecha', tasks: [] };
                    acc[key].tasks.push(task);
                    return acc;
                  }
                  const monthKey = date.substring(0, 7); // YYYY-MM
                  const monthDate = new Date(date + 'T12:00:00');
                  const label = monthDate.toLocaleDateString('es-ES', { month: 'long', year: 'numeric' });
                  if (!acc[monthKey]) acc[monthKey] = { label: label.charAt(0).toUpperCase() + label.slice(1), tasks: [] };
                  acc[monthKey].tasks.push(task);
                  return acc;
                }, {});
                
                // Ordenar meses (más recientes primero)
                const sortedMonths = Object.entries(tasksByMonth).sort((a, b) => b[0].localeCompare(a[0]));
                
                return (
                  <>
                    {/* Sección de Pendientes */}
                    {pendingTasks.length > 0 && (
                      <div>
                        <div className="flex items-center gap-2 mb-3 sticky top-0 bg-background py-2 z-10">
                          <div className="w-2 h-2 rounded-full bg-amber-500" />
                          <h3 className="font-semibold text-amber-700 dark:text-amber-400">
                            Pendientes ({pendingTasks.length})
                          </h3>
                        </div>
                        <div className="space-y-2 pl-4 border-l-2 border-amber-200 dark:border-amber-800">
                          {pendingTasks.map((task) => (
                            <TaskCard key={task.id} task={task} />
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Sección de Completadas por mes */}
                    {sortedMonths.length > 0 && (
                      <div className="space-y-4">
                        <div className="flex items-center gap-2 sticky top-0 bg-background py-2 z-10">
                          <div className="w-2 h-2 rounded-full bg-green-500" />
                          <h3 className="font-semibold text-green-700 dark:text-green-400">
                            Completadas ({completedTasks.length})
                          </h3>
                        </div>
                        
                        {sortedMonths.map(([monthKey, { label, tasks }]) => (
                          <details key={monthKey} className="group" open={monthKey === sortedMonths[0]?.[0]}>
                            <summary className="flex items-center gap-2 cursor-pointer select-none py-2 px-3 bg-muted/50 rounded-lg hover:bg-muted transition-colors">
                              <ChevronRight className="w-4 h-4 transition-transform group-open:rotate-90" />
                              <span className="font-medium">{label}</span>
                              <span className="text-sm text-muted-foreground">({tasks.length})</span>
                            </summary>
                            <div className="space-y-2 mt-2 pl-4 border-l-2 border-green-200 dark:border-green-800">
                              {tasks.map((task) => (
                                <TaskCard key={task.id} task={task} />
                              ))}
                            </div>
                          </details>
                        ))}
                      </div>
                    )}
                    
                    {pendingTasks.length === 0 && sortedMonths.length === 0 && (
                      <div className="text-center py-8 text-muted-foreground">
                        No hay tareas que mostrar
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          )}
        </>
      ) : (
        /* Calendar View */
        <div className="space-y-6">
          {/* Calendar Navigation */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Button variant="outline" size="icon" onClick={goToPrevMonth} data-testid="calendar-prev-month">
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button variant="outline" size="icon" onClick={goToNextMonth} data-testid="calendar-next-month">
                <ChevronRight className="w-4 h-4" />
              </Button>
              <Button variant="outline" size="sm" onClick={goToToday} data-testid="calendar-today-btn">
                Hoy
              </Button>
            </div>
            <div className="flex items-center gap-4 text-sm">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-red-500" />
                <span className="text-muted-foreground">Pendientes</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-green-500" />
                <span className="text-muted-foreground">Completadas</span>
              </div>
            </div>
          </div>

          {/* Calendar Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Calendars */}
            <div className="lg:col-span-2">
              <Card>
                <CardContent className="p-6">
                  {/* Mobile: Single month */}
                  <div className="lg:hidden">
                    <CalendarMonth date={currentMonth} />
                  </div>
                  
                  {/* Desktop: Two months */}
                  <div className="hidden lg:grid lg:grid-cols-2 lg:gap-8">
                    <CalendarMonth date={currentMonth} />
                    <CalendarMonth date={getNextMonth(currentMonth)} />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Selected Date Tasks */}
            <div className="lg:col-span-1">
              <Card className="h-full">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg">
                    {selectedDate 
                      ? new Date(selectedDate + 'T12:00:00').toLocaleDateString('es-ES', { 
                          weekday: 'long', 
                          day: 'numeric', 
                          month: 'long' 
                        })
                      : 'Selecciona una fecha'}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {!selectedDate ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <CalendarIcon className="w-10 h-10 mx-auto mb-3 opacity-30" />
                      <p className="text-sm">Haz clic en una fecha para ver las tareas</p>
                    </div>
                  ) : selectedDateTasks.length === 0 ? (
                    <div className="text-center py-8">
                      <CheckSquare className="w-10 h-10 mx-auto mb-3 text-muted-foreground/30" />
                      <p className="text-sm text-muted-foreground mb-4">No hay tareas para esta fecha</p>
                      <Button variant="outline" size="sm" onClick={() => isEditor && setDialogOpen(true)} disabled={!isEditor}>
                        <Plus className="w-4 h-4 mr-2" />
                        Crear tarea
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {selectedDateTasks.map((task) => (
                        <TaskCard key={task.id} task={task} compact />
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Sin fecha (Feb 2026): tasks without a due_date used to vanish
              from the calendar entirely. Now they get a dedicated tray so
              voice-note-generated tasks and manually-created ones without a
              date remain actionable. */}
          {(() => {
            const undated = getTasksWithoutDate();
            if (undated.length === 0) return null;
            return (
              <Card className="border-amber-200 bg-amber-50/30" data-testid="tasks-without-date">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-amber-600" />
                    Sin fecha <span className="text-sm text-muted-foreground font-normal">({undated.length})</span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-xs text-muted-foreground mb-3">
                    Estas tareas no tienen fecha asignada, por lo que no aparecen en las celdas del calendario. Haz clic sobre una para editarla y ponerle fecha.
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {undated.slice(0, 12).map((task) => (
                      <TaskCard key={task.id} task={task} compact draggable />
                    ))}
                  </div>
                  <p className="text-[11px] text-muted-foreground mt-3 italic">
                    💡 Arrastra una tarjeta sobre un día del calendario para asignarle esa fecha.
                  </p>
                  {undated.length > 12 && (
                    <p className="text-xs text-muted-foreground mt-2">
                      + {undated.length - 12} más — cámbiate a la vista de lista para verlas todas.
                    </p>
                  )}
                </CardContent>
              </Card>
            );
          })()}

          {/* Upcoming Tasks */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">Próximas tareas pendientes</CardTitle>
            </CardHeader>
            <CardContent>
              {(() => {
                const upcomingTasks = tasks
                  .filter(t => {
                    const clientType = getTaskClientType(t);
                    if (clientType && !matchesClientTypeFilter(clientType, clientTypeFilter)) return false;
                    return isOpen(t.status) && t.due_date && t.due_date >= today;
                  })
                  .sort((a, b) => a.due_date.localeCompare(b.due_date))
                  .slice(0, 5);
                
                if (upcomingTasks.length === 0) {
                  return (
                    <p className="text-sm text-muted-foreground text-center py-4">
                      No hay tareas pendientes próximas
                    </p>
                  );
                }

                return (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {upcomingTasks.map((task) => (
                      <TaskCard key={task.id} task={task} compact />
                    ))}
                  </div>
                );
              })()}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Task Dialog */}
      <TaskDialog 
        open={dialogOpen} 
        onOpenChange={handleDialogClose}
        onSuccess={handleDialogSuccess}
        task={editingTask}
      />

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteConfirm} onOpenChange={(open) => !open && setDeleteConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar Tarea</AlertDialogTitle>
            <AlertDialogDescription>
              ¿Estás seguro de que deseas eliminar "{deleteConfirm?.title}"? Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={deleting}
            >
              {deleting ? 'Eliminando...' : 'Eliminar'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Task Completion Dialog */}
      <TaskCompletionDialog
        open={completionDialogOpen}
        onOpenChange={setCompletionDialogOpen}
        task={completingTask}
        onSuccess={handleCompletionSuccess}
      />

      {/* Task Detail Dialog */}
      <TaskDetailDialog
        open={detailDialogOpen}
        onOpenChange={setDetailDialogOpen}
        task={viewingTask}
        companyName={viewingTask ? getCompanyName(viewingTask.company_id) : ''}
      />

      {/* Reminder-by-email dialog */}
      <RemindDialog
        open={remindDialogOpen}
        onOpenChange={setRemindDialogOpen}
        items={remindItems}
        onSent={handleReminderSent}
      />
    </div>
  );
};

export default Tasks;
